/**
 * @file CardTemplatePicker.tsx
 * @description Выбор шаблона карточки товара (phase19.16 Р2а;
 * uiux-design-r2a §2; db-design-r2a, ответ 5): «Классика», «Текст на фото»,
 * «Фото слева», «Компактная» — карточки-радиокнопки со схемой в цветах сайта
 * (переменные ролей `--site-color-<роль>` ставит эта обёртка из черновика
 * оформления) и строкой «для чего» из реестра shared `PRODUCT_CARD_TEMPLATES`.
 */

import { PRODUCT_CARD_TEMPLATES, type ProductCardTemplate, type SiteColors } from "@chaiberi/shared";
import type { CSSProperties, ReactNode } from "react";

import { OptionCards } from "../OptionCards";

const role = (name: string) => `var(--site-color-${name}, currentColor)`;

/** Строки текста карточки: название, цена, кнопка. */
function Lines({ x, y, w, light = false, button = "text" }: { x: number; y: number; w: number; light?: boolean; button?: "text" | "icon" }) {
  const ink = light ? role("onDark") : role("text");
  return (
    <g>
      <rect x={x} y={y} width={w * 0.8} height={3} rx={1} fill={ink} />
      <rect x={x} y={y + 6} width={w * 0.4} height={3} rx={1} fill={role("accent")} />
      {button === "text" ? <rect x={x} y={y + 12} width={w} height={6} rx={2} fill={role("button")} /> : <circle cx={x + w - 4} cy={y + 7} r={4} fill={role("button")} />}
    </g>
  );
}

/** Схема шаблона карточки 64×48. */
const SCHEMES: Readonly<Record<ProductCardTemplate, ReactNode>> = {
  classic: (
    <g>
      <rect x={20} y={2} width={24} height={20} rx={2} fill={role("panelBg")} />
      <Lines x={20} y={25} w={24} />
    </g>
  ),
  overlay: (
    <g>
      <rect x={18} y={2} width={28} height={44} rx={2} fill={role("darkBg")} />
      <Lines x={21} y={26} w={22} light />
    </g>
  ),
  horizontal: (
    <g>
      <rect x={6} y={10} width={22} height={28} rx={2} fill={role("panelBg")} />
      <Lines x={32} y={14} w={26} />
    </g>
  ),
  compact: (
    <g>
      <rect x={22} y={6} width={20} height={18} rx={2} fill={role("panelBg")} />
      <Lines x={22} y={28} w={20} button="icon" />
    </g>
  ),
};

/** Переменные ролей сайта для схем. */
function roleVars(colors: SiteColors | null): CSSProperties {
  if (!colors) return {};
  return Object.fromEntries(Object.entries(colors).map(([name, hex]) => [`--site-color-${name}`, hex])) as CSSProperties;
}

interface Props {
  value: ProductCardTemplate;
  onChange: (template: ProductCardTemplate) => void;
  colors: SiteColors | null;
}

export function CardTemplatePicker({ value, onChange, colors }: Props) {
  return (
    <div style={roleVars(colors)}>
      <OptionCards
        aria-label="Шаблон карточки товара"
        columns={2}
        value={value}
        onChange={onChange}
        options={PRODUCT_CARD_TEMPLATES.map((t) => ({
          value: t.key,
          label: t.label,
          hint: t.hint,
          preview: (
            <svg viewBox="0 0 64 48" className="h-12 w-16" aria-hidden>
              {SCHEMES[t.key]}
            </svg>
          ),
        }))}
      />
    </div>
  );
}
